// src/hooks/useSurveyAccess.ts
import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";

export type Capability = "view" | "analytics" | "edit" | "manage";

type Role = "owner" | "editor" | "viewer" | "admin" | "none";

type AccessResult = {
  allowed: boolean;
  role: Role;
  survey: any | null; // you can narrow if you like
};

function isPublished(data: any): boolean {
  return (
    data?.status === "published" ||
    data?.published === true ||
    data?.isPublished === true
  );
}

function computeRole(
  surveyData: any,
  uid: string | null,
  accountRole: "admin" | "user" | undefined
): Role {
  if (!uid) return "none";
  if (accountRole === "admin") return "admin";

  const ownerId = surveyData?.ownerId ?? surveyData?.userId;
  if (ownerId && ownerId === uid) return "owner";

  const roles = (surveyData?.roles ?? {}) as Record<string, "editor" | "viewer">;
  if (roles[uid] === "editor") return "editor";
  if (roles[uid] === "viewer") return "viewer";

  return "none";
}

function roleAllows(capability: Capability, role: Role, published: boolean): boolean {
  switch (capability) {
    case "manage":
      // Only owners and admins can access management screens
      return role === "owner" || role === "admin";
    case "edit":
      // Owners, admins, editors
      return role === "owner" || role === "admin" || role === "editor";
    case "analytics":
      // Owners, admins, editors, viewers
      return role === "owner" || role === "admin" || role === "editor" || role === "viewer";
    case "view":
      // Public if published; otherwise requires being in ACL (owner/admin/editor/viewer)
      if (published) return true;
      return role === "owner" || role === "admin" || role === "editor" || role === "viewer";
    default:
      return false;
  }
}

export function useSurveyAccess(surveyId: string | undefined, capability: Capability) {
  const [loading, setLoading] = useState<boolean>(true);
  const [result, setResult] = useState<AccessResult>({
    allowed: false,
    role: "none",
    survey: null,
  });

  const uid = auth.currentUser?.uid ?? null;

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      try {
        if (!surveyId) {
          if (!cancelled) setResult({ allowed: false, role: "none", survey: null });
          return;
        }

        // Load survey
        const surveySnap = await getDoc(doc(db, "surveys", surveyId));
        if (!surveySnap.exists()) {
          if (!cancelled) setResult({ allowed: false, role: "none", survey: null });
          return;
        }
        const surveyData = surveySnap.data();

        // Load account role (admin/user) — safe to read due to your rules
        let accountRole: "admin" | "user" | undefined = undefined;
        if (uid) {
          try {
            const userSnap = await getDoc(doc(db, "users", uid));
            if (userSnap.exists()) {
              const v = userSnap.data() as any;
              accountRole = (v?.accountRole === "admin" ? "admin" : "user") as "admin" | "user";
            }
          } catch {
            // ignore; default undefined -> treated as "user"
          }
        }

        const role = computeRole(surveyData, uid, accountRole);
        const published = isPublished(surveyData);
        const allowed = roleAllows(capability, role, published);

        if (!cancelled) {
          setResult({
            allowed,
            role,
            survey: { id: surveyId, ...surveyData },
          });
        }
      } catch (e) {
        if (!cancelled) setResult({ allowed: false, role: "none", survey: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [surveyId, uid, capability]);

  return { loading, ...result };
}
